/**
 * Admin mutations using React Relay.
 *
 * `inviteAdminToSystem` and `inviteAdminToOrganization` *are* the add-admin
 * flows: the add-admin pages call them instead of the legacy REST endpoints, so
 * ant owns admin creation there (and sends the set-password email). The other
 * mutations still run *additively* alongside the REST admin flow
 * (`api/admin.tsx`), which stays the source of truth for them during the
 * migration.
 *
 * - inviteAdminToSystem: creates the admin account when the email is new (ant
 *   emails the set-password link) or links an existing one, writes the system
 *   `access` and the email-notification flags, and grants the requested roles,
 *   all in one transaction.
 *   NOTE: this op returns `AdminUser!` directly (no `{ result, errors }`
 *   payload), so failures surface via onError / top-level GraphQL errors.
 * - inviteAdminToOrganization: creates the admin account when the email is new
 *   (ant emails the set-password link) or links an existing one, creates the
 *   organization membership with its `config`, and grants roles by name (e.g.
 *   the baseline "Read Only" role), all in one transaction. The legacy REST
 *   `POST /organization/<id>/admin` could only link an existing account.
 *   Returns `AdminUser!` directly (no `{ result, errors }` payload), so failures
 *   surface via onError / top-level GraphQL errors.
 * - assignSystemRole: grant a single system role by name.
 * - removeAdminSystemRoles: best-effort removal of multiple system roles at once
 *   by name. Returns the removed roles directly (no `{ result, errors }`
 *   payload), so failures surface via onError / top-level GraphQL errors.
 * - removeAdminFromSystem: removes an admin (by email) from a system entirely,
 *   revoking every role and permission they hold there. Replaces the legacy REST
 *   `archive` flow. Returns `AdminUser!` directly (no `{ result, errors }`
 *   payload), so failures surface via onError / top-level GraphQL errors.
 * - removeAdminFromOrganization: removes an admin (by email) from an organization
 *   entirely, revoking every role and permission they hold there. Replaces the
 *   legacy REST DELETE flow. Returns `AdminUser!` directly (no `{ result, errors }`
 *   payload), so failures surface via onError / top-level GraphQL errors.
 *
 * Note: Run `npm run relay` after editing GraphQL queries to regenerate type
 * artifacts under __generated__.
 */

import { useCallback } from 'react';
import { useMutation, commitMutation } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';

// ============================================================================
// GraphQL Mutations
// ============================================================================

const InviteAdminToSystemMutation = graphql`
  mutation useAdminMutationsInviteToSystemMutation($input: InviteAdminToSystemInput!) {
    inviteAdminToSystem(input: $input) {
      id
      email
      firstName
      lastName
    }
  }
`;

const InviteAdminToOrganizationMutation = graphql`
  mutation useAdminMutationsInviteToOrganizationMutation(
    $input: InviteAdminToOrganizationInput!
  ) {
    inviteAdminToOrganization(input: $input) {
      id
      email
      firstName
      lastName
    }
  }
`;

const AssignOrgRoleMutation = graphql`
  mutation useAdminMutationsAssignOrgRoleMutation($input: AssignOrgRoleInput!) {
    assignOrgRole(input: $input) {
      result {
        id
        adminId
        organizationId
        roleId
        role {
          id
          name
        }
      }
      errors {
        message
        fields
        code
        shortMessage
        vars
      }
    }
  }
`;

const RemoveAdminOrganizationRoleMutation = graphql`
  mutation useAdminMutationsRemoveOrgRoleMutation($id: ID!) {
    removeAdminOrganizationRole(id: $id) {
      result {
        id
        roleId
        role {
          id
          name
        }
      }
      errors {
        message
        fields
        code
        shortMessage
        vars
      }
    }
  }
`;

const AssignSystemRoleMutation = graphql`
  mutation useAdminMutationsAssignSystemRoleMutation($input: AssignSystemRoleInput!) {
    assignSystemRole(input: $input) {
      result {
        id
        adminId
        systemId
        roleId
        role {
          id
          name
        }
      }
      errors {
        message
        fields
        code
        shortMessage
        vars
      }
    }
  }
`;

const RemoveAdminSystemRolesMutation = graphql`
  mutation useAdminMutationsRemoveSystemRolesMutation(
    $input: RemoveAdminSystemRolesInput!
  ) {
    removeAdminSystemRoles(input: $input) {
      id
      roleId
      role {
        id
        name
      }
    }
  }
`;

const RemoveAdminFromSystemMutation = graphql`
  mutation useAdminMutationsRemoveFromSystemMutation(
    $input: RemoveAdminFromSystemInput!
  ) {
    removeAdminFromSystem(input: $input) {
      id
      email
      firstName
      lastName
    }
  }
`;

const RemoveAdminFromOrganizationMutation = graphql`
  mutation useAdminMutationsRemoveFromOrganizationMutation(
    $input: RemoveAdminFromOrganizationInput!
  ) {
    removeAdminFromOrganization(input: $input) {
      id
      email
      firstName
      lastName
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export interface AdminMutationError {
  code?: string | null;
  fields?: readonly string[] | null;
  message?: string | null;
  shortMessage?: string | null;
  vars?: unknown;
}

export interface MutationResult<T> {
  data: T | null;
  errors: AdminMutationError[];
  isSuccess: boolean;
}

export interface InviteAdminToSystemInput {
  email: string;
  systemId: string;
  access?: number | null;
  firstName?: string | null;
  lastName?: string | null;
  phone?: number | null;
  /** Email the admin about new pending members of the system. */
  pendingUserEmails?: boolean | null;
  /** Email the admin about new support tickets (problem reports) in the system. */
  errorReportEmails?: boolean | null;
  applicationId?: string | null;
  roleNames?: string[] | null;
  roleIds?: string[] | null;
  permissionNames?: string[] | null;
  permissionIds?: string[] | null;
}

export interface InviteAdminToOrganizationInput {
  email: string;
  organizationId: string;
  firstName?: string | null;
  lastName?: string | null;
  applicationId?: string | null;
  config?: unknown;
  roleNames?: string[] | null;
  roleIds?: string[] | null;
  permissionNames?: string[] | null;
  permissionIds?: string[] | null;
}

export interface AssignSystemRoleInput {
  adminId: string;
  systemId: string;
  roleName?: string | null;
  roleId?: string | null;
  applicationId?: string | null;
}

export interface AssignOrgRoleInput {
  adminId: string;
  organizationId: string;
  roleName?: string | null;
  roleId?: string | null;
  applicationId?: string | null;
}

export interface RemoveAdminSystemRolesInput {
  adminId: string;
  systemId: string;
  roleNames?: string[] | null;
  roleIds?: string[] | null;
  applicationId?: string | null;
}

export interface RemoveAdminFromSystemInput {
  email: string;
  systemId: string;
}

export interface RemoveAdminFromOrganizationInput {
  email: string;
  organizationId: string;
}

// ============================================================================
// Imperative API (used from existing toast/promise flows)
// ============================================================================

export function inviteAdminToSystemMutation(
  input: InviteAdminToSystemInput
): Promise<MutationResult<unknown>> {
  return new Promise((resolve) => {
    commitMutation<any>(relayEnvironment, {
      mutation: InviteAdminToSystemMutation,
      variables: { input },
      onCompleted: (response: any, errors) => {
        const result = response?.inviteAdminToSystem ?? null;
        const gqlErrors = (errors ?? []).map((e) => ({ message: e.message }));
        resolve({
          data: result,
          errors: gqlErrors,
          isSuccess: gqlErrors.length === 0 && result != null,
        });
      },
      onError: (error: Error) => {
        resolve({
          data: null,
          errors: [{ message: error.message }],
          isSuccess: false,
        });
      },
    });
  });
}

export function inviteAdminToOrganizationMutation(
  input: InviteAdminToOrganizationInput
): Promise<MutationResult<unknown>> {
  return new Promise((resolve) => {
    commitMutation<any>(relayEnvironment, {
      mutation: InviteAdminToOrganizationMutation,
      variables: { input },
      onCompleted: (response: any, errors) => {
        const result = response?.inviteAdminToOrganization ?? null;
        const gqlErrors = (errors ?? []).map((e) => ({ message: e.message }));
        resolve({
          data: result,
          errors: gqlErrors,
          isSuccess: gqlErrors.length === 0 && result != null,
        });
      },
      onError: (error: Error) => {
        resolve({
          data: null,
          errors: [{ message: error.message }],
          isSuccess: false,
        });
      },
    });
  });
}

export function assignOrgRoleMutation(
  input: AssignOrgRoleInput
): Promise<MutationResult<unknown>> {
  return new Promise((resolve) => {
    commitMutation<any>(relayEnvironment, {
      mutation: AssignOrgRoleMutation,
      variables: { input },
      onCompleted: (response: any) => {
        const payload = response?.assignOrgRole;
        const errors = (payload?.errors ?? []) as AdminMutationError[];
        resolve({
          data: payload?.result ?? null,
          errors,
          isSuccess: errors.length === 0 && payload?.result != null,
        });
      },
      onError: (error: Error) => {
        resolve({
          data: null,
          errors: [{ message: error.message }],
          isSuccess: false,
        });
      },
    });
  });
}

// Soft-deletes (revokes) a single organization role grant by its
// AdminOrganizationRole row id. The backend `removeAdminOrganizationRole`
// mutation takes only that id, so callers resolve the row id from the admin
// lookup (`fetchOrgAdminUserByEmail`) before calling.
export function removeAdminOrganizationRoleMutation(
  id: string
): Promise<MutationResult<unknown>> {
  return new Promise((resolve) => {
    commitMutation<any>(relayEnvironment, {
      mutation: RemoveAdminOrganizationRoleMutation,
      variables: { id },
      onCompleted: (response: any) => {
        const payload = response?.removeAdminOrganizationRole;
        const errors = (payload?.errors ?? []) as AdminMutationError[];
        resolve({
          data: payload?.result ?? null,
          errors,
          isSuccess: errors.length === 0 && payload?.result != null,
        });
      },
      onError: (error: Error) => {
        resolve({
          data: null,
          errors: [{ message: error.message }],
          isSuccess: false,
        });
      },
    });
  });
}

export function assignSystemRoleMutation(
  input: AssignSystemRoleInput
): Promise<MutationResult<unknown>> {
  return new Promise((resolve) => {
    commitMutation<any>(relayEnvironment, {
      mutation: AssignSystemRoleMutation,
      variables: { input },
      onCompleted: (response: any) => {
        const payload = response?.assignSystemRole;
        const errors = (payload?.errors ?? []) as AdminMutationError[];
        resolve({
          data: payload?.result ?? null,
          errors,
          isSuccess: errors.length === 0 && payload?.result != null,
        });
      },
      onError: (error: Error) => {
        resolve({
          data: null,
          errors: [{ message: error.message }],
          isSuccess: false,
        });
      },
    });
  });
}

export function removeAdminSystemRolesMutation(
  input: RemoveAdminSystemRolesInput
): Promise<MutationResult<unknown>> {
  return new Promise((resolve) => {
    commitMutation<any>(relayEnvironment, {
      mutation: RemoveAdminSystemRolesMutation,
      variables: { input },
      onCompleted: (response: any, errors) => {
        const removed = response?.removeAdminSystemRoles ?? null;
        const gqlErrors = (errors ?? []).map((e) => ({ message: e.message }));
        resolve({
          data: removed,
          errors: gqlErrors,
          isSuccess: gqlErrors.length === 0,
        });
      },
      onError: (error: Error) => {
        resolve({
          data: null,
          errors: [{ message: error.message }],
          isSuccess: false,
        });
      },
    });
  });
}

export function removeAdminFromSystemMutation(
  input: RemoveAdminFromSystemInput
): Promise<MutationResult<unknown>> {
  return new Promise((resolve) => {
    commitMutation<any>(relayEnvironment, {
      mutation: RemoveAdminFromSystemMutation,
      variables: { input },
      onCompleted: (response: any, errors) => {
        const result = response?.removeAdminFromSystem ?? null;
        const gqlErrors = (errors ?? []).map((e) => ({ message: e.message }));
        resolve({
          data: result,
          errors: gqlErrors,
          isSuccess: gqlErrors.length === 0 && result != null,
        });
      },
      onError: (error: Error) => {
        resolve({
          data: null,
          errors: [{ message: error.message }],
          isSuccess: false,
        });
      },
    });
  });
}

export function removeAdminFromOrganizationMutation(
  input: RemoveAdminFromOrganizationInput
): Promise<MutationResult<unknown>> {
  return new Promise((resolve) => {
    commitMutation<any>(relayEnvironment, {
      mutation: RemoveAdminFromOrganizationMutation,
      variables: { input },
      onCompleted: (response: any, errors) => {
        const result = response?.removeAdminFromOrganization ?? null;
        const gqlErrors = (errors ?? []).map((e) => ({ message: e.message }));
        resolve({
          data: result,
          errors: gqlErrors,
          isSuccess: gqlErrors.length === 0 && result != null,
        });
      },
      onError: (error: Error) => {
        resolve({
          data: null,
          errors: [{ message: error.message }],
          isSuccess: false,
        });
      },
    });
  });
}

// ============================================================================
// Hooks (React hook variants, for consistency with other V2 features)
// ============================================================================

export function useInviteAdminToSystemMutation() {
  const [commit, isInFlight] = useMutation<any>(InviteAdminToSystemMutation);

  const inviteAdminToSystem = useCallback(
    (input: InviteAdminToSystemInput) =>
      new Promise<MutationResult<unknown>>((resolve) => {
        commit({
          variables: { input },
          onCompleted: (response: any, errors) => {
            const result = response?.inviteAdminToSystem ?? null;
            const gqlErrors = (errors ?? []).map((e) => ({ message: e.message }));
            resolve({
              data: result,
              errors: gqlErrors,
              isSuccess: gqlErrors.length === 0 && result != null,
            });
          },
          onError: (error: Error) => {
            resolve({
              data: null,
              errors: [{ message: error.message }],
              isSuccess: false,
            });
          },
        });
      }),
    [commit]
  );

  return { inviteAdminToSystem, isLoading: isInFlight };
}

export function useInviteAdminToOrganizationMutation() {
  const [commit, isInFlight] = useMutation<any>(InviteAdminToOrganizationMutation);

  const inviteAdminToOrganization = useCallback(
    (input: InviteAdminToOrganizationInput) =>
      new Promise<MutationResult<unknown>>((resolve) => {
        commit({
          variables: { input },
          onCompleted: (response: any, errors) => {
            const result = response?.inviteAdminToOrganization ?? null;
            const gqlErrors = (errors ?? []).map((e) => ({ message: e.message }));
            resolve({
              data: result,
              errors: gqlErrors,
              isSuccess: gqlErrors.length === 0 && result != null,
            });
          },
          onError: (error: Error) => {
            resolve({
              data: null,
              errors: [{ message: error.message }],
              isSuccess: false,
            });
          },
        });
      }),
    [commit]
  );

  return { inviteAdminToOrganization, isLoading: isInFlight };
}

export function useAssignSystemRoleMutation() {
  const [commit, isInFlight] = useMutation<any>(AssignSystemRoleMutation);

  const assignSystemRole = useCallback(
    (input: AssignSystemRoleInput) =>
      new Promise<MutationResult<unknown>>((resolve) => {
        commit({
          variables: { input },
          onCompleted: (response: any) => {
            const payload = response?.assignSystemRole;
            const errors = (payload?.errors ?? []) as AdminMutationError[];
            resolve({
              data: payload?.result ?? null,
              errors,
              isSuccess: errors.length === 0 && payload?.result != null,
            });
          },
          onError: (error: Error) => {
            resolve({
              data: null,
              errors: [{ message: error.message }],
              isSuccess: false,
            });
          },
        });
      }),
    [commit]
  );

  return { assignSystemRole, isLoading: isInFlight };
}

export function useRemoveAdminSystemRolesMutation() {
  const [commit, isInFlight] = useMutation<any>(RemoveAdminSystemRolesMutation);

  const removeAdminSystemRoles = useCallback(
    (input: RemoveAdminSystemRolesInput) =>
      new Promise<MutationResult<unknown>>((resolve) => {
        commit({
          variables: { input },
          onCompleted: (response: any, errors) => {
            const removed = response?.removeAdminSystemRoles ?? null;
            const gqlErrors = (errors ?? []).map((e) => ({ message: e.message }));
            resolve({
              data: removed,
              errors: gqlErrors,
              isSuccess: gqlErrors.length === 0,
            });
          },
          onError: (error: Error) => {
            resolve({
              data: null,
              errors: [{ message: error.message }],
              isSuccess: false,
            });
          },
        });
      }),
    [commit]
  );

  return { removeAdminSystemRoles, isLoading: isInFlight };
}

export function useRemoveAdminFromSystemMutation() {
  const [commit, isInFlight] = useMutation<any>(RemoveAdminFromSystemMutation);

  const removeAdminFromSystem = useCallback(
    (input: RemoveAdminFromSystemInput) =>
      new Promise<MutationResult<unknown>>((resolve) => {
        commit({
          variables: { input },
          onCompleted: (response: any, errors) => {
            const result = response?.removeAdminFromSystem ?? null;
            const gqlErrors = (errors ?? []).map((e) => ({ message: e.message }));
            resolve({
              data: result,
              errors: gqlErrors,
              isSuccess: gqlErrors.length === 0 && result != null,
            });
          },
          onError: (error: Error) => {
            resolve({
              data: null,
              errors: [{ message: error.message }],
              isSuccess: false,
            });
          },
        });
      }),
    [commit]
  );

  return { removeAdminFromSystem, isLoading: isInFlight };
}

export function useRemoveAdminFromOrganizationMutation() {
  const [commit, isInFlight] = useMutation<any>(RemoveAdminFromOrganizationMutation);

  const removeAdminFromOrganization = useCallback(
    (input: RemoveAdminFromOrganizationInput) =>
      new Promise<MutationResult<unknown>>((resolve) => {
        commit({
          variables: { input },
          onCompleted: (response: any, errors) => {
            const result = response?.removeAdminFromOrganization ?? null;
            const gqlErrors = (errors ?? []).map((e) => ({ message: e.message }));
            resolve({
              data: result,
              errors: gqlErrors,
              isSuccess: gqlErrors.length === 0 && result != null,
            });
          },
          onError: (error: Error) => {
            resolve({
              data: null,
              errors: [{ message: error.message }],
              isSuccess: false,
            });
          },
        });
      }),
    [commit]
  );

  return { removeAdminFromOrganization, isLoading: isInFlight };
}
